/**
 * Home page component - Landing page with navigation to CPU sections
 */

import React, { useEffect } from 'react'
import { useNavigation } from '../hooks/useNavigation'
import { TOOL_CONFIG } from '../config/toolConfig'
import type { ThemeColor } from '../types/theme'
import ComponentBadge from '../components/icon/ComponentBadge'
import { ComponentType } from '../models/pc-build-model'

// Static Tailwind maps keyed on ThemeColor (dynamic class strings break JIT)
const TOOL_HEADER_BG: Partial<Record<ThemeColor, string>> = {
  indigo: 'bg-indigo-500 hover:bg-indigo-600',
  violet: 'bg-violet-500 hover:bg-violet-600',
  emerald: 'bg-emerald-500 hover:bg-emerald-600',
}
const TOOL_LINK_COLOR: Partial<Record<ThemeColor, string>> = {
  indigo: 'text-indigo-600 hover:text-indigo-800',
  violet: 'text-violet-600 hover:text-violet-800',
  emerald: 'text-emerald-600 hover:text-emerald-800',
}

const HomePage: React.FC = () => {
  const { navigate } = useNavigation()

  useEffect(() => {
    // Update page title
    document.title = 'Check My Rig - PC Building Companion'
  }, [])

  // Component navigation cards - displayed in 2 columns
  const navigationCards = [
    {
      title: 'Browse CPUs',
      description: 'Explore our comprehensive database of CPU specifications',
      path: '/cpus',
      icon: <ComponentBadge type={ComponentType.CPU} bare size="lg" />,
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      title: 'Browse Motherboards',
      description: 'Discover detailed motherboard specifications and features',
      path: '/motherboards',
      icon: <ComponentBadge type={ComponentType.MOTHERBOARD} bare size="lg" />,
      color: 'bg-red-500 hover:bg-red-600'
    },
    {
      title: 'Browse GPUs',
      description: 'Explore graphics card specifications and performance data',
      path: '/gpus',
      icon: <ComponentBadge type={ComponentType.GPU} bare size="lg" />,
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      title: 'Browse Memory',
      description: 'Explore DDR4 and DDR5 memory module specifications',
      path: '/memory',
      icon: <ComponentBadge type={ComponentType.MEMORY} bare size="lg" />,
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      title: 'Browse Storage',
      description: 'Explore NVMe SSDs, SATA SSDs, and HDDs with detailed specifications',
      path: '/storage',
      icon: <ComponentBadge type={ComponentType.STORAGE} bare size="lg" />,
      color: 'bg-gray-500 hover:bg-gray-600'
    },
    {
      title: 'Browse PSUs',
      description: 'Explore power supply specifications and efficiency ratings',
      path: '/psus',
      icon: <ComponentBadge type={ComponentType.PSU} bare size="lg" />,
      color: 'bg-yellow-500 hover:bg-yellow-600'
    },
    {
      title: 'Browse Cases',
      description: 'Explore PC case specifications and form factors',
      path: '/cases',
      icon: <ComponentBadge type={ComponentType.CASE} bare size="lg" />,
      color: 'bg-orange-500 hover:bg-orange-600'
    },
    {
      title: 'Browse CPU Coolers',
      description: 'Explore air and liquid cooling solutions for optimal performance',
      path: '/coolers',
      icon: <ComponentBadge type={ComponentType.CPU_COOLER} bare size="lg" />,
      color: 'bg-teal-500 hover:bg-teal-600'
    }
  ]

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Check My Rig
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your ultimate PC building companion. Browse detailed specifications, check compatibility,
            and compare components for CPUs, GPUs, motherboards, memory, storage, and more.
          </p>
        </div>

        {/* Compatibility Checker - Full Width */}
        <div className="max-w-4xl mx-auto">
          <div
            onClick={() => navigate(TOOL_CONFIG.COMPATIBILITY.path)}
            className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer overflow-hidden"
          >
            <div className={`${TOOL_HEADER_BG[TOOL_CONFIG.COMPATIBILITY.themeColor]} p-6 text-white text-center`}>
              <div className="flex justify-center mb-4">
                <TOOL_CONFIG.COMPATIBILITY.Icon className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Compatibility Checker</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600">Validate CPU-Motherboard compatibility and get suggestions</p>
              <div className={`mt-4 flex items-center justify-center ${TOOL_LINK_COLOR[TOOL_CONFIG.COMPATIBILITY.themeColor]}`}>
                <span className="font-medium">Get Started</span>
                <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* My Check & My Builder - 2 columns */}
        <div className="grid md:grid-cols-2 gap-6 mb-8 max-w-4xl mx-auto mt-6">
          {([TOOL_CONFIG.MY_CHECK, TOOL_CONFIG.MY_BUILDER] as const).map(tool => (
            <div
              key={tool.path}
              onClick={() => navigate(tool.path)}
              className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer overflow-hidden"
            >
              <div className={`${TOOL_HEADER_BG[tool.themeColor]} p-5 text-white text-center`}>
                <div className="flex justify-center mb-3">
                  <tool.Icon className="w-8 h-8" />
                </div>
                <h2 className="text-xl font-bold">{tool.label}</h2>
              </div>
              <div className="p-5">
                <p className="text-gray-600 text-sm">
                  {tool.path === '/my-check'
                    ? 'Check compatibility using your saved favorite components'
                    : 'Build and save PC configurations from your favorites'}
                </p>
                <div className={`mt-3 flex items-center ${TOOL_LINK_COLOR[tool.themeColor]}`}>
                  <span className="font-medium text-sm">Open</span>
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Component Navigation Cards - 2 Column Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
          {navigationCards.map((card, index) => (
            <div
              key={index}
              onClick={() => navigate(card.path)}
              className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer overflow-hidden"
            >
              <div className={`${card.color} p-6 text-white text-center`}>
                <div className="flex justify-center mb-4">
                  {card.icon}
                </div>
                <h2 className="text-2xl font-bold mb-2">{card.title}</h2>
              </div>
              <div className="p-6">
                <p className="text-gray-600">{card.description}</p>
                <div className="mt-4 flex items-center text-blue-600 hover:text-blue-800">
                  <span className="font-medium">Get Started</span>
                  <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Features Section */}
        <div className="bg-white rounded-lg shadow p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900">Advanced Search</h3>
              <p className="text-sm text-gray-600">Filter by specifications, price, performance metrics</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H9z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900">Side-by-Side Compare</h3>
              <p className="text-sm text-gray-600">Compare multiple CPUs with detailed analysis</p>
            </div>
            <div className="text-center">
              <div className="bg-red-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900">Favorites</h3>
              <p className="text-sm text-gray-600">Save and organize your preferred CPU models</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900">Performance Data</h3>
              <p className="text-sm text-gray-600">Comprehensive specifications and benchmarks</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomePage